import { FC, useMemo } from 'react';
import { Outlet } from 'react-router';
import {
  Box,
  GlobalStyles,
  ThemeProvider,
  type Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { createRenterTheme } from 'src/theme/renterTheme';
import NewVersionValidation from 'src/components/NewVersionValidation';
import SessionExpiryAlert from 'src/components/SessionExpiryAlert';
import ImpersonatedUserBanner from 'src/UI/Banners/ImpersonatedUserBanner';
import HouseholdCompositionModal from 'src/components/NewRenterInterface/HouseholdComposition/HouseholdCompositionModal';
import ProfileHouseholdCompositionModal from 'src/components/NewRenterInterface/HouseholdComposition/ProfileHouseholdCompositionModal';
import useAuth from 'src/hooks/useAuth';
import CertificationUpdateManager from 'src/components/NewRenterInterface/CertificationUpdateManager';
import UserUpdateManager from 'src/components/NewRenterInterface/UserUpdateManager';
import CertificationAlertsHeader from 'src/components/Compliance/Certification/CertificationAlerts/components/CertificationAlertsHeader';
import { RootState } from 'src/redux/rootReducerType';
import RenterNavBar, {
  NAV_WIDTH_COLLAPSED,
  NAV_WIDTH_EXPANDED,
} from 'src/components/NewRenterInterface/NavBar/RenterNavBar';
import RenterTopBar from 'src/components/NewRenterInterface/NavBar/RenterTopBar';
import TranslationBanner from 'src/components/NewRenterInterface/TranslationBanner';
import GoogleTranslateElement from 'src/components/NewRenterInterface/GoogleTranslateElement';
import RenterContentErrorBoundary from 'src/components/NewRenterInterface/RenterContentErrorBoundary';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';

const RentersLayout: FC = () => {
  const { user } = useAuth();
  const baseTheme = useTheme();
  const colorTheme = useSelector(
    (state: RootState) => state.newRenterUI.colorTheme
  );
  const renterTheme = useMemo(
    () => createRenterTheme(baseTheme, colorTheme),
    [baseTheme, colorTheme]
  );
  const isMobile = useMediaQuery(baseTheme.breakpoints.down('md'));
  const navExpanded = useSelector(
    (state: RootState) => state.newRenterUI.expanded
  );
  const topBarHeight = useSelector(
    (state: RootState) => state.newRenterUI.topBarHeight
  );
  const bottomNavHeight = useSelector(
    (state: RootState) => state.newRenterUI.bottomNavHeight
  );

  return (
    <ThemeProvider theme={renterTheme}>
      <>
        <GlobalStyles
          styles={{
            'html, body': {
              overscrollBehaviorY: 'none',
            },
          }}
        />
        <GoogleTranslateElement />
        <NewVersionValidation />
        {user?.impersonator && <ImpersonatedUserBanner />}
        <CertificationAlertsHeader
          context={'renter'}
          isUserActive
          sx={{ zIndex: 1500 }}
        />
        <CertificationUpdateManager />
        <UserUpdateManager />
        <PendingVerificationsModal />
        {isMobile && <RenterTopBar />}
        <RenterNavBar />
        <Box
          component="main"
          id="renter-main-scroll"
          sx={(theme: Theme) => ({
            position: 'fixed',
            top: { xs: topBarHeight, md: 0 },
            bottom: { xs: bottomNavHeight, md: 0 },
            left: isMobile
              ? 0
              : navExpanded
              ? NAV_WIDTH_EXPANDED
              : NAV_WIDTH_COLLAPSED,
            right: 0,
            overflowY: 'auto',
            transition: isMobile
              ? 'none'
              : theme.transitions.create('left', {
                  easing: theme.transitions.easing.sharp,
                  duration: navExpanded
                    ? theme.transitions.duration.enteringScreen
                    : theme.transitions.duration.leavingScreen,
                }),
          })}
        >
          <TranslationBanner />
          <RenterContentErrorBoundary>
            <Outlet />
          </RenterContentErrorBoundary>
        </Box>
        <SessionExpiryAlert />
        <HouseholdCompositionModal />
        <ProfileHouseholdCompositionModal />
      </>
    </ThemeProvider>
  );
};

export default RentersLayout;
